import CssBaseline from "@material-ui/core/CssBaseline";
import AppContainer, {
  Auth,
  useCurrentTenant,
} from "@qpr/client-library/dist/esm";
import "App.css";
import "init";
import AppBarContext from "qpr-base/appbar/BusinessPortalAppBarContext";
import useAppBar from "qpr-base/appbar/BusinessPortalAppBarHook";
import interceptorService from "qpr-base/axios/interceptor";
import * as conf from "qpr-base/configuration";
import { AppCtxProvider } from "qpr-base/context/AppCtxProvider";
import { ReportCtxProvider } from "qpr-base/context/ReportCtxProvider";
import { ScrollCtxProvider } from "qpr-base/context/ScrollContextProvider";
import UserContextProvider from "qpr-base/context/UserContextProvider";
import LanguageSwitchView from "qpr-base/languageswitch/LanguageSwitchView";
import { RTLProvider } from "qpr-base/languageswitch/index";
import LogoutPage from "qpr-base/logout/LogoutPage";
import LoginPage from "qpr-base/pages/LoginPage";
import startBatcherService from "qpr-base/querybuilder/QueryBatcher";
import Loader from "qpr-base/ui/components/Loader";
import ErrorBoundary, { ErrorBoundaryProps } from "qpr-base/ui/errors/index";
import React, { Suspense } from "react";
import { Route, BrowserRouter as Router, Switch, useParams } from "react-router-dom";
import "resources/init";
import { SelectedReportContextProvider } from "state/SelectedReportContext";
import Theme from "theme/Theme";
import "typeface-open-sans";
import "typeface-roboto";
import QPRToast from "MyDashboards/components/QPRToast/QPRToast";
import Routes from "./Routes";
import BusinessPortalAppBar from "./components/toolbars/BusinessPortalAppBar";
import { getDefaultPeriod } from "qpr-base/context/periodDefaults";
import SessionKeepAlive from "qpr-base/auth/SessionKeepAlive";

interceptorService.setupInterceptors();
startBatcherService(200);

const Pages = () => {
  const tenant = useCurrentTenant();
  const { tenantSymbol } = useParams<{ tenantSymbol?: string }>();

  // Derive base model id from tenant configuration
  const tenantModelId = tenant.metricsModelId
    ? tenant.metricsModelId.split(".")[1]
    : "";

  // Default model to 2026 (2106935752) for this tenant.
  const defaultModelId =
    // 2025 model id is 1954172446 (827938453 is legacy/old)
    tenantModelId === "1954172446" || tenantModelId === "827938453" || !tenantModelId
      ? "2106935752"
      : tenantModelId;

  conf.setDefaults("defaultModel", defaultModelId);

  // Default reporting period: last closed month (same as periodDefaults / PeriodSelector).
  conf.setDefaults("defaultPeriod", getDefaultPeriod());

  conf.setToolbar("LanguageSwitchView", () => <LanguageSwitchView />);

  return (
    <div>
      <Theme>
        <Auth loginPage={<LoginPage />}>
          <SessionKeepAlive />
          <UserContextProvider>
            <ReportCtxProvider>
              <AppCtxProvider>
                <AppBarContext.Provider value={useAppBar()}>
                  <SelectedReportContextProvider>
                    <RTLProvider>
                      <ErrorBoundary>
                        <BusinessPortalAppBar />
                        <QPRToast />
                        <ErrorBoundary
                          {...({ variant: "inline", homeTo: tenantSymbol ? `/${tenantSymbol}` : "/" } as ErrorBoundaryProps)}
                        >
                          <ScrollCtxProvider>
                            <Routes />
                          </ScrollCtxProvider>
                        </ErrorBoundary>
                      </ErrorBoundary>
                    </RTLProvider>
                  </SelectedReportContextProvider>
                </AppBarContext.Provider>
              </AppCtxProvider>
            </ReportCtxProvider>
          </UserContextProvider>
        </Auth>
      </Theme>
    </div>
  );
};
const App = () => {
  return (
    <Router>
      <AppContainer>
        <Switch>
          <Route path="/logout" component={LogoutPage} />
          <Route>
            <CssBaseline />
            <Suspense fallback={<Loader />}>
              <Pages />
            </Suspense>
          </Route>
        </Switch>
      </AppContainer>
    </Router>
  );
};
export default App;
